'use client';

import { useEffect, useState, useRef } from 'react';
import Image from "next/image";
import Link from "next/link";
import { useIntl } from 'react-intl';
import { Menu, MenuLink, MenuTitle, MenuItem, MenuHeading, MenuIcon, MenuSub, MenuArrow, MenuBullet } from '../menu';
import clsx from 'clsx';
import { KeenIcon } from '../keenicons';
import { MENU_SIDEBAR } from '../menu/menu.config';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';
import { ScreenLoader } from '../loaders/ScreenLoader';

export default function Sidebar() {
  const intl = useIntl()
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isSidebarHovered, setIsSidebarHovered] = useState(false);
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const { formatMessage } = useIntl();
  const { settings, settingsLoading, settingserror } = useSelector((state: any) => state.customer);

  const sidebarRef = useRef<HTMLDivElement>(null);
  const linkPl = 'ps-[10px]';
  const linkPr = 'pe-[10px]';
  const linkPy = 'py-[6px]';
  const itemsGap = 'gap-0.5';
  const subLinkPy = 'py-[8px]';
  const rightOffset = 'me-[-10px]';
  const iconWidth = 'w-[20px]';
  const iconSize = 'text-lg';
  const accordionLinkGap = [
    'gap-[10px]',
    'gap-[14px]',
    'gap-[5px]',
    'gap-[5px]',
    'gap-[5px]',
    'gap-[5px]'
  ];


  useEffect(() => {
    const handleToggle = () => {
      setIsSidebarVisible(prev => !prev);
    };
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node) &&
        window.innerWidth < 1024
      ) {
        setIsSidebarVisible(false);
      }
    };
    window.addEventListener('toggle-sidebar', handleToggle);
    if (isSidebarVisible && window.innerWidth < 1024) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      window.removeEventListener('toggle-sidebar', handleToggle);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSidebarVisible]);

  useEffect(() => {
    if (settingserror !== null && settingserror !== undefined) {
        toast.error(settingserror || intl.formatMessage({ id: 'SOMETHING_WENT_WRONG' }));
    }
}, [settingserror]);

  const toggleSidebar = () => {
    setIsSidebarCollapsed(prev => {
      const newState = !prev;
      if (newState) {
        document.body.classList.add('sidebar-collapse');
      } else {
        document.body.classList.remove('sidebar-collapse');
      }
 
      return newState;
    });
  };

  if (settingsLoading) {
    return <ScreenLoader />;
}
  const buttonClass = isSidebarCollapsed ? 'active' : '';

  return (
    <div
      className={`sidebar bg-light lg:border-e lg:border-e-gray-200 dark:border-e-coal-100 lg:fixed lg:top-0 lg:bottom-0 lg:z-20 lg:flex flex-col items-stretch shrink-0 dark:bg-coal-600 ${isSidebarVisible ? 'block' : 'hidden'} lg:block`}
      id="sidebar" ref={sidebarRef}
    >
      {/* Header */}
      <div className="sidebar-header lg:flex items-center relative justify-between px-3 lg:px-6 shrink-0">

        {/* logo for light mode  */}
        <Link className="dark:hidden" href="/">
          <Image
            className="default-logo h-[40px] max-w-none"
            src={
              settings?.options?.logo?.original
                ? (process.env.NEXT_PUBLIC_IMAGE_BASE_URL || '') + settings.options.logo.original
                : "/assets/media/app/default-logo.svg"
            }
            alt="Main Logo"
            priority
            width={130}
            height={40}
          />
          <Image
            className="small-logo h-[40px] max-w-none object-contain"
            src={
              settings?.options?.logo?.original
                ? (process.env.NEXT_PUBLIC_IMAGE_BASE_URL || '') + settings.options.favicon.original
                : "/assets/media/app/mini-logo.png"
            }
            alt="Mini Logo"
            priority
            width={30}
            height={30}
          />
        </Link>
        {/* logo for dark mode  */}
        <Link className="hidden dark:block" href="/">
          <Image
            className="default-logo h-[40px] max-w-none object-contain"
            src={
              settings?.options?.dark_logo?.original
                ? (process.env.NEXT_PUBLIC_IMAGE_BASE_URL || '') + settings.options.dark_logo.original
                : "/assets/media/app/default-logo.svg"
            }
            alt="Main Logo"
            priority
            width={130}
            height={40}
          />
          <Image
            className="small-logo h-[40px] max-w-none"
            src={
              settings?.options?.dark_logo?.original
                ? (process.env.NEXT_PUBLIC_IMAGE_BASE_URL || '') + settings.options.favicon.original
                : "/assets/media/app/mini-logo.png"
            }
            alt="Mini Logo"
            priority
            width={30}
            height={30}
          />
        </Link>

        <button
          className={`btn btn-icon btn-icon-md size-[30px] rounded-lg border bg-light text-gray-500 hover:text-gray-700 toggle absolute start-full top-2/4 rtl:translate-x-2/4 -translate-x-2/4 -translate-y-2/4 border-gray-200 dark:border-gray-300 ${buttonClass}`}
          onClick={toggleSidebar}
        >
          <KeenIcon icon="black-left-line" className={`transition-all duration-300 ${isSidebarCollapsed ? 'rtl:rotate-180' : 'ltr:rotate-180'}`}
          />
        </button>
      </div>

      {/* Content */}
      <div className="sidebar-content flex grow shrink-0 py-5 pe-2">
        <div className="scrollable-y-hover grow shrink-0 flex ps-2 lg:ps-5 pe-1 lg:pe-3 overflow-y-auto">
          <div className="menu flex flex-col grow gap-0.5">
            <Menu
              highlight={true}
              multipleExpand={false}
              className={clsx('flex flex-col grow', itemsGap)}
            >{MENU_SIDEBAR.map((item, index) => (
              <MenuItem key={index} toggle={item.children ? 'accordion' : undefined}>
                <MenuLink
                  path={item.path ?? '#'}
                  className={clsx(
                    item.children
                      ? 'flex items-center grow cursor-pointer border border-transparent'
                      : 'border border-transparent menu-item-active:bg-secondary-active dark:menu-item-active:bg-coal-300 dark:menu-item-active:border-gray-100 menu-item-active:rounded-lg hover:bg-secondary-active dark:hover:bg-coal-300 dark:hover:border-gray-100 hover:rounded-lg',
                    accordionLinkGap[0],
                    linkPl,
                    linkPr,
                    linkPy
                  )}
                >
                  <MenuIcon className={clsx('items-start text-gray-600 dark:text-gray-500 menu-item-active:text-[#Fa981c] menu-link-hover:!text-[#Fa981c]', iconWidth)}>
                    <KeenIcon icon={`ki-filled ki-${item.icon}`} className={iconSize} />
                  </MenuIcon>
                  <MenuTitle className="text-sm font-medium text-gray-800 menu-item-active:text-[#Fa981c] menu-link-hover:!text-[#Fa981c] ">
                    {formatMessage({ id: item.title })}
                  </MenuTitle>
                  {item.children && (
                    <MenuArrow className="text-gray-400 w-[20px] shrink-0 justify-end ms-1 me-[-10px]">
                      <KeenIcon icon="plus" className="text-2xs menu-item-show:hidden" />
                      <KeenIcon icon="minus" className="text-2xs hidden menu-item-show:inline-flex" />
                    </MenuArrow>
                  )}
                </MenuLink>

                {item.children && (
                  <MenuSub className='relative before:absolute before:top-0 before:bottom-0 before:border-s before:border-gray-200 gap-0.5 before:start-[20px] ps-[10px]'>
                    {item.children.map((subItem, subIndex) => (
                      <MenuItem key={subIndex}>
                        <MenuLink
                          path={subItem.path ?? '#'}
                          className={clsx(
                            'border border-transparent items-center grow menu-item-active:bg-secondary-active dark:menu-item-active:bg-coal-300 dark:menu-item-active:border-gray-100 menu-item-active:rounded-lg hover:bg-secondary-active dark:hover:bg-coal-300 dark:hover:border-gray-100 hover:rounded-lg gap-[14px] ps-[10px]',
                            subLinkPy,
                            linkPr
                          )}
                        >
                          <MenuBullet className="flex w-[6px] -start-[3px] rtl:start-0 relative before:absolute before:top-0 before:size-[6px] before:rounded-full rtl:before:translate-x-1/2 before:-translate-y-1/2 menu-item-active:before:bg-[#Fa981c] menu-item-hover:before:bg-[#Fa981c]" />
                          <MenuTitle className="text-2sm font-normal text-gray-800 menu-item-active:text-[#Fa981c] menu-item-active:font-semibold menu-link-hover:!text-[#Fa981c]">
                            {subItem.title}
                          </MenuTitle>
                        </MenuLink>
                      </MenuItem>
                    ))}
                  </MenuSub>
                )}
              </MenuItem>
            ))}
            </Menu>
          </div>
        </div>
      </div>
    </div>
  );
}
